import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { registerServiceWorker } from "./lib/pwa";
import { installVersionCheck } from "./lib/version-check";
import { installThrowQueueFlusher } from "./lib/throwQueue";
import "./index.css";

registerServiceWorker();
// Drain throws queued by offline/optimistic solo games to the server as
// soon as connectivity allows (also re-fires on online + focus).
installThrowQueueFlusher();
// Bulletproof "am I on the latest bundle?" check. Compares the bundle
// hash we shipped with against the hash the server is currently
// serving; if they differ, unregisters SWs + clears caches + reloads.
// Runs on boot, every 60s, on focus, and on online. PWA caching and
// the browser's voluntary SW polling can't get in the way.
installVersionCheck();

const queryClient = new QueryClient({
  defaultOptions: {
    // networkMode "always": never gate fetches on navigator.onLine.
    // The default "online" mode PAUSES queries and mutations whenever
    // the browser reports offline — and installed PWAs (especially on
    // mobile) routinely get onlineManager stuck "offline" after being
    // backgrounded/idle. A paused mutation makes `mutateAsync` hang
    // forever, which wedged "Start scoring" into a permanent Saving…
    // state until the app was fully restarted. We'd rather always
    // attempt the request and let it error visibly (api() times out).
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      networkMode: "always",
    },
    mutations: { networkMode: "always" },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
